import React, { useRef, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { Link } from "gatsby";
import PropTypes from "prop-types";
import classNames from "classnames";

import RichTextBlock from "./RichTextBlock";
import InvisibleReCAPTCHA from "./InvisibleReCAPTCHA";

import { useSiteMetadata } from "../hooks/useSiteMetadata";
import { isEmailDomainAllow, isValidEmail, isValidName } from "../utils/utils";

const LaborSavingsReportForm = ({
    variant,
    lead,
    successMessage,
    submitButtonLabel,
    source,
}) => {
    const [facilityName, setFacilityName] = useState("");
    const [jobTitle, setJobTitle] = useState("");
    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [privacyCheckbox, setPrivacyCheckbox] = useState(false);
    const [showEmailDomainError, setShowEmailDomainError] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [hasSubmitAttempt, setHasSubmitAttempt] = useState(false);
    const [errors, setErrors] = useState({});
    const [submissionError, setSubmissionError] = useState(null);

    const setValidity = (field, valid) =>
        setErrors({ ...errors, [field]: !valid });

    const updateEmail = e => {
        setEmail(e.target.value);
        setValidity("email", isValidEmail(e.target.value));
    };

    const isValidEmailDomain = email => {
        const isDomainAllowed = isEmailDomainAllow(email);
        setShowEmailDomainError(!isDomainAllowed);
        return isDomainAllowed;
    };

    const isValid = () => {
        const _errors = {
            facilityName: !isValidName(facilityName),
            jobTitle: !isValidName(jobTitle),
            fullName: !isValidName(fullName),
            email: !isValidEmail(email) || !isValidEmailDomain(email),
        };
        setErrors(_errors);
        return !Object.keys(_errors).some(key => _errors[key]);
    };

    const {
        endpoints: { demo: endpointUrl },
    } = useSiteMetadata();

    const postFormData = async reCAPTCHAToken => {
        const formData = {
            job_title: jobTitle,
            facility_name: facilityName,
            full_name: fullName,
            email,
            source: source || "b2b_misc",
            tags: ["labor_savings_report"],
            recaptcha_token: reCAPTCHAToken,
            referer: window?.location?.href || "",
        };

        const response = await fetch(endpointUrl, {
            method: "POST",
            mode: "cors",
            cache: "no-cache",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(formData),
        });
        return response.json();
    };

    const submitForm = reCAPTCHAToken => {
        setSubmitting(true);
        if (reCAPTCHAToken && isValid()) {
            postFormData(reCAPTCHAToken)
                .then(data => {
                    setSubmissionError(data.error ? data.msg : null);
                    setSubmitted(true);
                })
                .catch(error => console.error(error));
        } else {
            setHasSubmitAttempt(true);
        }
        reCAPTCHARef.current.reset();
        setSubmitting(false);
    };

    const reCAPTCHARef = useRef(null);

    const executeReCAPTCHA = async e => {
        e.preventDefault();
        try {
            const token = await reCAPTCHARef.current.executeAsync();
            submitForm(token);
        } catch (error) {
            console.error(error);
        }
    };

    if (submitted && !submissionError) {
        return (
            <div className="py-5">
                <RichTextBlock content={successMessage} />
            </div>
        );
    }

    return (
        <div className="labor-savings-report">
            {lead && (
                <div className="mb-4">
                    <RichTextBlock content={lead} />
                </div>
            )}

            <Form
                key="labor-savings-report-form"
                className={classNames("labor-savings-report-form", {
                    "has-submit-attempt": hasSubmitAttempt,
                })}
                onSubmit={executeReCAPTCHA}
            >
                <Row>
                    <Col
                        md={{ span: variant === "stacked" ? 6 : 12, order: 1 }}
                        className="mb-4"
                    >
                        <Form.Control
                            type="text"
                            size="lg"
                            required
                            value={fullName}
                            onChange={e => setFullName(e.target.value)}
                            placeholder="Full Name*"
                            className={classNames({
                                invalid: !!errors["fullName"],
                            })}
                        />
                    </Col>

                    <Col md={{ span: 6, order: 2 }} className="mb-4">
                        <Form.Control
                            type="text"
                            size="lg"
                            required
                            value={facilityName}
                            onChange={e => setFacilityName(e.target.value)}
                            placeholder="Hospital Name*"
                            className={classNames({
                                invalid: !!errors["facilityName"],
                            })}
                        />
                    </Col>

                    <Col
                        md={{
                            span: variant === "stacked" ? 6 : 12,
                            order: variant === "stacked" ? 3 : 4,
                        }}
                        className="mb-4"
                    >
                        <Form.Control
                            type="email"
                            size="lg"
                            required
                            value={email}
                            onChange={updateEmail}
                            placeholder="Email*"
                            className={classNames({
                                invalid: !!errors["email"],
                            })}
                        />
                        {showEmailDomainError && (
                            <small className="text-danger">
                                Please enter a valid work email address.
                            </small>
                        )}
                    </Col>

                    <Col
                        md={{ span: 6, order: variant === "stacked" ? 4 : 3 }}
                        className="mb-4"
                    >
                        <Form.Control
                            type="text"
                            size="lg"
                            required
                            value={jobTitle}
                            onChange={e => setJobTitle(e.target.value)}
                            placeholder="Work Title*"
                            className={classNames({
                                invalid: !!errors["jobTitle"],
                            })}
                        />
                    </Col>
                </Row>

                <p>
                    Nurses looking for information?{" "}
                    <Link to="/contact/">Click Here.</Link>
                </p>

                {variant !== "stacked" && (
                    <Form.Check size="lg" type="checkbox" className="ms-1">
                        <Form.Check.Input
                            type="checkbox"
                            required
                            aria-label="Disclaimer"
                            checked={privacyCheckbox}
                            onChange={e => setPrivacyCheckbox(e.target.checked)}
                        />
                        <Form.Check.Label className="compliance ms-1">
                            I agree to receive promotional and transactional
                            messages from ShiftMed. Our privacy policy can be
                            found <Link to="/privacy/">here</Link>.
                        </Form.Check.Label>
                    </Form.Check>
                )}

                <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    disabled={submitting}
                >
                    {submitButtonLabel || "Submit"}
                </Button>

                {submissionError && (
                    <Alert className="my-3" variant="danger">
                        {submissionError}
                    </Alert>
                )}
                <InvisibleReCAPTCHA innerRef={reCAPTCHARef} />
            </Form>
        </div>
    );
};

LaborSavingsReportForm.propTypes = {
    theme: PropTypes.string,
    lead: PropTypes.shape({ raw: PropTypes.string }),
    successMessage: PropTypes.shape({ raw: PropTypes.string }),
    styleType: PropTypes.string,
    headerForm: PropTypes.bool,
    source: PropTypes.string,
};

export default LaborSavingsReportForm;
